import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Workflow, RefreshCw, ArrowRight, Lock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const StateChip = ({ label, color }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
    style={{ backgroundColor: `${color || '#64748b'}22`, color: color || '#334155' }}
  >
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color || '#64748b' }} />
    {label}
  </span>
);

/**
 * Reusable per-record workflow activity widget. Shows the current workflow
 * state, the next available transitions, and a timeline of state changes.
 *
 *   <WorkflowActivity doctype="vendor" recordId={vendor.vendor_number} />
 *
 * When the DocType supports inline transitions (backend `can_apply`) and the
 * caller has the required role, the next-action chips become a one-click
 * approval console: clicking a chip applies the transition via
 * POST /api/workflows/apply/{doctype}/{record_id}, prompting for a comment
 * first when the transition `requires_comment`. Renders nothing when the
 * DocType has no active workflow and no state history, so it is safe to drop
 * onto any record page. Pass an optional `onAction(t)` to override behaviour.
 */
const WorkflowActivity = ({ doctype, recordId, currentState, onAction }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyingState, setApplyingState] = useState(null); // to_state currently posting
  const [commentFor, setCommentFor] = useState(null);       // transition awaiting a comment
  const [commentText, setCommentText] = useState('');

  const load = useCallback(async () => {
    if (!doctype || !recordId) return;
    setLoading(true);
    try {
      const qs = currentState ? `?current_state=${encodeURIComponent(currentState)}` : '';
      const res = await axios.get(`${API}/api/workflows/activity/${doctype}/${recordId}${qs}`);
      setData(res.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [doctype, recordId, currentState]);

  useEffect(() => { load(); }, [load]);

  const applyTransition = useCallback(async (t, comment) => {
    setApplyingState(t.to_state);
    try {
      await axios.post(`${API}/api/workflows/apply/${doctype}/${recordId}`, {
        to_state: t.to_state,
        comment: comment || null,
      });
      toast.success(`${t.action_label} — moved to ${t.to_state_label || t.to_state}`);
      setCommentFor(null);
      setCommentText('');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not apply transition';
      toast.error(detail);
    } finally {
      setApplyingState(null);
    }
  }, [doctype, recordId, load]);

  const handleChip = (t) => {
    if (onAction) { onAction(t); return; }
    if (!data?.can_apply || !t.allowed_for_current_user) return;
    if (t.requires_comment) {
      setCommentText('');
      setCommentFor(t);
    } else {
      applyTransition(t, null);
    }
  };

  // Nothing to show for DocTypes without a workflow and without state history
  if (!loading && (!data || (!data.has_workflow && (data.timeline || []).length === 0))) {
    return null;
  }

  const transitions = data?.available_transitions || [];
  const timeline = data?.timeline || [];
  const actionsLive = !!onAction || !!data?.can_apply;

  return (
    <Card className="p-6" data-testid="workflow-activity">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Workflow className="w-5 h-5 text-sky-600" />
            Workflow Activity
          </h3>
          {data?.workflow_name && (
            <p className="text-xs text-gray-400 mt-0.5">{data.workflow_name}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {data?.current_state && (
            <StateChip label={data.current_state_label || data.current_state} color={data.current_state_color} />
          )}
          <Button variant="ghost" size="sm" onClick={load} data-testid="wfa-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          {transitions.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-medium text-gray-500 mb-2">NEXT ACTIONS</p>
              <div className="flex flex-wrap gap-2">
                {transitions.map((t, i) => {
                  const actionable = t.allowed_for_current_user && actionsLive;
                  const posting = applyingState === t.to_state;
                  return (
                    <Button
                      key={i}
                      size="sm"
                      variant={t.allowed_for_current_user ? 'default' : 'outline'}
                      disabled={!actionable || !!applyingState}
                      title={t.allowed_for_current_user ? '' : `Requires role: ${(t.allowed_roles || []).join(', ') || '—'}`}
                      onClick={() => actionable && handleChip(t)}
                      data-testid={`wfa-action-${t.to_state}`}
                      className={t.allowed_for_current_user ? 'bg-sky-600 hover:bg-sky-700' : ''}
                    >
                      {posting
                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        : (!t.allowed_for_current_user && <Lock className="w-3 h-3 mr-1" />)}
                      {t.action_label}
                      <ArrowRight className="w-3 h-3 mx-1" />
                      {t.to_state_label || t.to_state}
                    </Button>
                  );
                })}
              </div>
              {!actionsLive && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Perform the transition from this record's own controls — the roles allowed to action each step are shown above.
                </p>
              )}
            </div>
          )}

          <p className="text-xs font-medium text-gray-500 mb-2">STATE HISTORY</p>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-400">No state changes recorded yet.</p>
          ) : (
            <ol className="relative border-l-2 border-gray-100 ml-2">
              {[...timeline].reverse().map((ev, i) => (
                <li key={i} className="mb-5 ml-4" data-testid={`wfa-event-${i}`}>
                  <span
                    className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full ring-2 ring-white"
                    style={{ backgroundColor: ev.to_state_color || '#0ea5e9' }}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <StateChip label={ev.from_state_label || ev.from_state || '—'} color={ev.from_state_color} />
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <StateChip label={ev.to_state_label || ev.to_state} color={ev.to_state_color} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{ev.actor} · {fmtTime(ev.timestamp)}</p>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      <Dialog open={!!commentFor} onOpenChange={(o) => { if (!o) { setCommentFor(null); setCommentText(''); } }}>
        <DialogContent data-testid="wfa-comment-dialog">
          <DialogHeader>
            <DialogTitle>
              {commentFor?.action_label} → {commentFor?.to_state_label || commentFor?.to_state}
            </DialogTitle>
            <DialogDescription>
              This transition requires a comment. Add a note before you proceed.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Enter a comment…"
            rows={4}
            data-testid="wfa-comment-input"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCommentFor(null); setCommentText(''); }}
              data-testid="wfa-comment-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-sky-600 hover:bg-sky-700"
              disabled={!commentText.trim() || !!applyingState}
              onClick={() => applyTransition(commentFor, commentText.trim())}
              data-testid="wfa-comment-submit"
            >
              {applyingState ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WorkflowActivity;
